import type { FluidImpulse } from "../fluid/types";
import type { ForcingFrame, ForcingProgram } from "./types";

export interface WindTunnelForcingSettings {
  jetCount: number;
  openingHeight: number;
  radiusCells: number;
  radiusSpacingRatio: number;
  dyeAmount: number;
  inletX: number;
}

const DEFAULT_SETTINGS: WindTunnelForcingSettings = {
  jetCount: 5,
  openingHeight: 0.76,
  radiusCells: 10,
  radiusSpacingRatio: 0.1,
  dyeAmount: 0.034,
  inletX: 0.985,
};

const SMOKE_COLOUR: readonly [number, number, number] = [0.92, 0.92, 0.92];

/** A steady right-to-left wind tunnel visualized by fixed white smoke jets. */
export class WindTunnelForcingProgram implements ForcingProgram {
  private readonly settings: WindTunnelForcingSettings;

  constructor(settings: Partial<WindTunnelForcingSettings> = {}) {
    this.settings = { ...DEFAULT_SETTINGS, ...settings };
  }

  reset(_seed = 0): void {}

  update(frame: ForcingFrame): readonly FluidImpulse[] {
    const motionScale = frame.reducedMotion ? 0.22 : 1;
    const dyeScale = Math.min(1, frame.deltaTime * 60) * motionScale;
    const jetCount = Math.max(1, Math.floor(this.settings.jetCount));
    const jetSpacing =
      jetCount === 1
        ? 0
        : (clamp(this.settings.openingHeight, 0, 0.92) *
            frame.simulationSize.height) /
          (jetCount - 1);
    const radius = Math.max(
      this.settings.radiusCells,
      jetSpacing * this.settings.radiusSpacingRatio,
    );
    const impulses: FluidImpulse[] = [];

    for (let index = 0; index < jetCount; index += 1) {
      const y = jetPosition(index, jetCount, this.settings.openingHeight);

      impulses.push({
        point: [this.settings.inletX, y],
        velocity: [0, 0],
        radius,
        color: SMOKE_COLOUR,
        dyeAmount: this.settings.dyeAmount * dyeScale,
      });
    }

    return impulses;
  }
}

function jetPosition(index: number, count: number, openingHeight: number): number {
  if (count === 1) {
    return 0.5;
  }
  const height = clamp(openingHeight, 0, 0.92);
  return 0.5 - height * 0.5 + (height * index) / (count - 1);
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}
